import dayjs from "dayjs";

import {
  type CoverageInsuranceQuery,
  InsuranceCareCategory,
  InsuranceCheckField,
  InsuranceCheckFieldReason,
  InsuranceCheckProblem,
  InsuranceCheckRequirement,
  InsuranceCheckState,
  InsuranceCostShareSource,
  InsuranceCoverageType,
  InsuranceMembershipStatus,
  InsuranceNetwork,
  InsuranceNoPrimaryReason,
  InsurancePlanType,
  InsurancePositionKind,
  InsurancePositionScope,
  InsuranceRelationship,
  PlanYearStatus,
} from "@medbillai/generated-graphql-types";

import { v2 } from "@medbillai/schemas";

import { checkOutcome, known } from "../../../lib/insurance/checkOutcome";
import {
  DEDUCTIBLES,
  OUT_OF_POCKET,
  ownPositions,
} from "../../../lib/insurance/positions";
import { toTitleCase } from "../../../lib/strings";
import { shownPrice } from "../format";

type Insurance = CoverageInsuranceQuery["myInsurance"];
type Membership = Insurance["memberships"][number];
type Position = Membership["positions"][number];
type CostShare = Membership["costShares"][number];
type Amount = CostShare["copays"][number];

export type Figure = {
  met: number;
  limit: number | null;
  remaining: number | null;
};

type FigurePair = {
  individual: Figure | null;
  family: Figure | null;
};

export type NetworkFigures = {
  deductible: FigurePair;
  outOfPocket: FigurePair;
};

export type Tier = "individual" | "family";
export type Network = "inNetwork" | "outOfNetwork";

export type HeadlineFigure = Figure & {
  kind: "deductible" | "outOfPocket";
  tier: Tier;
};

export type ChartPoint = { at: Date; met: number };

export type DeductibleRule = "applies" | "doesNotApply" | "unknown";

/** A range when the payer lists several amounts and names none the default. */
export type PriceAmount =
  | { kind: "one"; amount: number; deductible: DeductibleRule }
  | { kind: "range"; min: number; max: number; deductible: DeductibleRule };

export type Price = {
  category: InsuranceCareCategory;
  /** Dollars per visit. */
  copay: PriceAmount | null;
  /** The user's share: 0.2 is 20%. */
  coinsurance: PriceAmount | null;
};

/** The check's lead movement, when it rose enough to tell the user. */
export type Movement = {
  kind: "deductible" | "outOfPocket";
  family: boolean;
  outOfNetwork: boolean;
  delta: number;
  /** When the check that saw it ran. */
  at: Date;
};

type PlanHealth = "ok" | "stale" | "failing";

export type PlanSummary = {
  membershipId: string;
  /** Whose plan it is, in title case: payers send names in capitals. */
  memberName: string;
  payerName: string;
  logoUrl: string | null;
  /** The payer's EDI ID, as its cards print it. */
  payerId: string | null;
  planName: string | null;
  /** `YYYY`, when the payer stated the plan year. */
  yearLabel: string | null;
  health: PlanHealth;
};

export type PlanContact = {
  key: "memberServices" | "nurseLine" | "mentalHealth" | "pharmacyHelp";
  label: string;
  phone: string;
};

export type CheckFieldIssue = {
  field: InsuranceCheckField;
  reason: InsuranceCheckFieldReason;
};

export type CoveragePlan = PlanSummary & {
  memberId: string | null;
  groupId: string | null;
  planType: InsurancePlanType | null;
  pharmacy: {
    rxBin: string | null;
    rxPcn: string | null;
    rxGroup: string | null;
  } | null;
  /** The phone numbers on the back of the card: member services, nurse line, mental health, pharmacy help. */
  contacts: PlanContact[];
  /** `YYYY-MM-DD`; null until a check answers. */
  planYear: { start: string | null; end: string | null } | null;
  /** When the payer last answered. */
  confirmedAt: Date | null;
  staleSince: Date | null;
  /** Someone else is on the user's policy, so the plan covers a family. */
  coversDependents: boolean;
  /** A dependent's figures, lent by the subscriber's plan: family figures only. */
  figuresFromSubscriber: boolean;
  /** The policyholder's first name when it isn't the user. */
  subscriberName: string | null;
  policyholderSelf: boolean;
  checkIssue: CheckFieldIssue[] | null;
  headline: HeadlineFigure | null;
  inNetwork: NetworkFigures;
  outOfNetwork: NetworkFigures | null;
  /** The headline figure's readings, oldest first. */
  history: ChartPoint[];
  /** Readings for each network, figure and tier, oldest first. */
  histories: Record<
    Network,
    Record<HeadlineFigure["kind"], Record<Tier, ChartPoint[]>>
  >;
  movement: Movement | null;
  prices: Price[];
  notCovered: InsuranceCareCategory[];
  notReported: InsuranceCareCategory[];
  /** No check has answered for the new plan year yet: totals are empty. */
  planYearEnded: boolean;
  /** A past plan, picked to look back at: its last figures stand. */
  ended: boolean;
  /** `YYYY-MM-DD`, when an ended plan's payer stated its last day. */
  endedOn: string | null;
  /** UNKNOWN until a payer answer confirms the membership. */
  status: InsuranceMembershipStatus;
  rootPrice: Price | null;
};

/** How long a check has gone unanswered; `useCoverage` times it. */
export type CheckingWait = "usual" | "long" | "stuck";

export type CoverageRead =
  | { kind: "setup"; missing: InsuranceCheckRequirement[] }
  | { kind: "checking"; wait: CheckingWait }
  | { kind: "uninsured" }
  | {
      kind: "noPlan";
      reason: InsuranceNoPrimaryReason;
      plans: PlanSummary[];
      memberIdKnown: boolean;
    }
  | {
      kind: "failing";
      problem: InsuranceCheckProblem | null;
      lastConfirmedAt: Date | null;
      plan: CoveragePlan | null;
      otherPlans: PlanSummary[];
    }
  | { kind: "plan"; plan: CoveragePlan; otherPlans: PlanSummary[] };

/** What you pay always lists these first, in this order, when priced. */
const CORE_SERVICES = [
  InsuranceCareCategory.PRIMARY_CARE,
  InsuranceCareCategory.SPECIALIST,
  InsuranceCareCategory.URGENT_CARE,
  InsuranceCareCategory.EMERGENCY_ROOM,
  InsuranceCareCategory.INPATIENT,
  InsuranceCareCategory.OUTPATIENT_SURGERY,
  InsuranceCareCategory.MENTAL_HEALTH,
  InsuranceCareCategory.TELEHEALTH,
  InsuranceCareCategory.LAB_WORK,
  InsuranceCareCategory.IMAGING,
];

const ROOT_PRICE_ORDER = [
  InsuranceCareCategory.URGENT_CARE,
  InsuranceCareCategory.PRIMARY_CARE,
  InsuranceCareCategory.SPECIALIST,
  InsuranceCareCategory.LAB_WORK,
  InsuranceCareCategory.TELEHEALTH,
];

/** `membershipId` picks the plan to lead with; the primary otherwise. */
export const readCoverage = (
  insurance: Insurance,
  membershipId?: string,
): CoverageRead => {
  const { checkStatus } = insurance;
  const outcome = checkOutcome(known(InsuranceCheckState, checkStatus.state));
  if (outcome === "pending") {
    return { kind: "checking", wait: "usual" };
  }
  if (outcome === "payerUninsured") {
    return { kind: "uninsured" };
  }
  if (outcome === "notStarted" || outcome === "needsInfo") {
    return {
      kind: "setup",
      missing: checkStatus.missing.map(r =>
        known(InsuranceCheckRequirement, r),
      ),
    };
  }
  const { memberships } = insurance;
  // A picked plan leads, unless it has ended and the user now has a primary.
  const picked = memberships.find(m => m.id === membershipId);
  const primary = memberships.find(m => m.primary);
  const hero =
    picked && !(picked.status === InsuranceMembershipStatus.INACTIVE && primary)
      ? picked
      : primary;
  const others = onePerPolicy(
    memberships.filter(
      m =>
        m !== hero &&
        m.status !== InsuranceMembershipStatus.INACTIVE &&
        isMedical(m) &&
        !(hero && samePolicy(m, hero)),
    ),
  );
  if (outcome === "checkFailed" || hero?.checkProblem) {
    const problem = hero ? hero.checkProblem : checkStatus.problem;
    return {
      kind: "failing",
      problem: problem ? known(InsuranceCheckProblem, problem) : null,
      lastConfirmedAt: hero?.confirmedAt ?? null,
      plan: hero ? planOf(hero, memberships) : null,
      otherPlans: others.map(summaryOf),
    };
  }
  if (hero) {
    return {
      kind: "plan",
      plan: planOf(hero, memberships),
      otherPlans: others.map(summaryOf),
    };
  }
  const reason = known(
    InsuranceNoPrimaryReason,
    insurance.noPrimaryReason ?? InsuranceNoPrimaryReason.UNRECOGNIZED,
  );
  return reason === InsuranceNoPrimaryReason.NONE_ON_FILE
    ? { kind: "setup", missing: [] }
    : {
        kind: "noPlan",
        reason,
        plans: onePerPolicy(memberships).map(summaryOf),
        memberIdKnown: memberships.some(m => m.memberId !== null),
      };
};

/** The household's rows on one policy are the same plan. A subscriber's own row has no `subscriberMemberId`. */
const samePolicy = (a: Membership, b: Membership): boolean => {
  const policy = (m: Membership) => m.subscriberMemberId ?? m.memberId;
  return (
    policy(a) !== null && policy(a) === policy(b) && a.payerName === b.payerName
  );
};

/** One row per policy; the user's own row stands for the household's. */
const onePerPolicy = (rows: Membership[]): Membership[] => {
  const ownFirst = [...rows].sort(
    (a, b) => Number(b.accountHolder) - Number(a.accountHolder),
  );
  const kept = ownFirst.filter(
    (m, i) => !ownFirst.slice(0, i).some(o => samePolicy(o, m)),
  );
  return rows.filter(m => kept.includes(m));
};

const isMedical = (m: Membership): boolean => {
  const type = known(InsuranceCoverageType, m.coverageType);
  return (
    type === InsuranceCoverageType.MEDICAL ||
    type === InsuranceCoverageType.UNRECOGNIZED
  );
};

/** "2026", or "2025–26" for a plan year that crosses January. */
const planYearLabel = (
  planYear: { start?: string | null; end?: string | null } | null | undefined,
): string | null => {
  const start = planYear?.start?.slice(0, 4);
  const end = planYear?.end?.slice(0, 4);
  if (!start) {
    return null;
  }
  return end && end !== start ? `${start}–${end.slice(2)}` : start;
};

const summaryOf = (m: Membership): PlanSummary => ({
  membershipId: m.id,
  memberName: [m.person.firstName, m.person.lastName]
    .join(" ")
    .split(/\s+/)
    .filter(part => part !== "")
    .map(toTitleCase)
    .join(" "),
  payerName: m.payerName,
  logoUrl: m.payer?.logoUrl ?? null,
  payerId: m.payer?.primaryPayerId ?? null,
  planName: m.plan.name ?? null,
  yearLabel: planYearLabel(m.planYear),
  health: m.checkProblem ? "failing" : m.stale ? "stale" : "ok",
});

const DEPENDENTS: InsuranceRelationship[] = [
  InsuranceRelationship.SPOUSE,
  InsuranceRelationship.CHILD,
  InsuranceRelationship.OTHER,
];

const planOf = (m: Membership, memberships: Membership[]): CoveragePlan => {
  const own = ownPositions(m);
  const deductible = headlineOf(own, DEDUCTIBLES, "deductible");
  const outOfPocket = headlineOf(own, OUT_OF_POCKET, "outOfPocket");
  const inNetwork = figuresOf(own, InsurancePositionScope.IN_NETWORK);
  const headline = maxLeads(inNetwork)
    ? (outOfPocket ?? deductible)
    : (deductible ?? outOfPocket);
  const shares = inNetworkOf(m.costShares);
  const prices = pricesOf(shares);
  const priced = new Set(prices.map(p => p.category));
  const notCovered = CORE_SERVICES.filter(
    c =>
      !priced.has(c) &&
      shares.some(s => s.category === c && s.covered === false),
  );
  return {
    ...summaryOf(m),
    memberId: m.memberId ?? null,
    groupId: m.groupId ?? null,
    planType: m.plan.type ? known(InsurancePlanType, m.plan.type) : null,
    pharmacy: m.pharmacy
      ? {
          rxBin: m.pharmacy.rxBin ?? null,
          rxPcn: m.pharmacy.rxPcn ?? null,
          rxGroup: m.pharmacy.rxGroup ?? null,
        }
      : null,
    contacts: contactsOf(m.contacts),
    planYear: m.planYear
      ? { start: m.planYear.start ?? null, end: m.planYear.end ?? null }
      : null,
    confirmedAt: m.confirmedAt ?? null,
    staleSince: m.stale ? (m.confirmedAt ?? null) : null,
    coversDependents: memberships.some(
      o =>
        (o === m || samePolicy(o, m)) &&
        o.coverageType === m.coverageType &&
        o.status === InsuranceMembershipStatus.ACTIVE &&
        DEPENDENTS.includes(known(InsuranceRelationship, o.relationship)),
    ),
    figuresFromSubscriber: m.figuresFromSubscriber,
    subscriberName:
      m.policyholder.self || !m.policyholder.firstName
        ? null
        : toTitleCase(m.policyholder.firstName),
    policyholderSelf: m.policyholder.self,
    checkIssue:
      m.checkIssue?.fields.map(f => ({
        field: known(InsuranceCheckField, f.field),
        reason: known(InsuranceCheckFieldReason, f.reason),
      })) ?? null,
    headline: headline?.figure ?? null,
    inNetwork,
    outOfNetwork: own.some(
      p =>
        known(InsurancePositionScope, p.scope) ===
        InsurancePositionScope.OUT_OF_NETWORK,
    )
      ? figuresOf(own, InsurancePositionScope.OUT_OF_NETWORK)
      : null,
    history: headline ? historyOf(headline.position) : [],
    histories: {
      inNetwork: networkHistories(own, InsurancePositionScope.IN_NETWORK),
      outOfNetwork: networkHistories(
        own,
        InsurancePositionScope.OUT_OF_NETWORK,
      ),
    },
    // An ended plan or year, or figures we can't vouch for, is no news.
    movement:
      m.status === InsuranceMembershipStatus.INACTIVE ||
      m.planYear?.status === PlanYearStatus.ENDED ||
      m.stale
        ? null
        : movementOf(own),
    prices,
    notCovered,
    notReported: CORE_SERVICES.filter(
      c => !priced.has(c) && !notCovered.includes(c),
    ),
    planYearEnded: m.planYear?.status === PlanYearStatus.ENDED,
    ended: m.status === InsuranceMembershipStatus.INACTIVE,
    endedOn:
      m.status === InsuranceMembershipStatus.INACTIVE &&
      m.plan.terminationDate &&
      m.plan.terminationDate <= dayjs().format("YYYY-MM-DD")
        ? m.plan.terminationDate
        : null,
    status: known(InsuranceMembershipStatus, m.status),
    rootPrice: rootPriceOf(prices, inNetwork),
  };
};

/**
 * What a visit costs today: before the deductible is met, only a price the
 * deductible doesn't apply to is one; past the out-of-pocket max, it's $0.
 */
const rootPriceOf = (
  prices: Price[],
  { deductible, outOfPocket }: NetworkFigures,
): Price | null => {
  const maxMet = outOfPocketMet(outOfPocket);
  const price = ROOT_PRICE_ORDER.flatMap(c =>
    prices.filter(
      p =>
        p.category === c &&
        (maxMet ||
          deductibleMet(deductible) ||
          shownPrice(p)?.amount.deductible === "doesNotApply"),
    ),
  )[0];
  if (!price || !maxMet) {
    return price ?? null;
  }
  return {
    category: price.category,
    copay: { kind: "one", amount: 0, deductible: "doesNotApply" },
    coinsurance: null,
  };
};

/** The check's lead movement, when it rose by the notify threshold. */
const movementOf = (own: Position[]): Movement | null => {
  const leads = own.flatMap(position => {
    // An ENDED reading carries no amount; what came before it was a figure
    // that has since ended.
    const ended = position.history.findIndex(
      r => r.metAmount === null || r.metAmount === undefined,
    );
    return (ended === -1 ? position.history : position.history.slice(0, ended))
      .filter(reading => reading.lead && typeof reading.delta === "number")
      .map(reading => ({ position, reading }));
  });
  const lead = leads.reduce<(typeof leads)[number] | undefined>(
    (newest, l) =>
      newest && newest.reading.observedAt >= l.reading.observedAt ? newest : l,
    undefined,
  );
  if (!lead?.reading.delta || lead.reading.delta < v2.LEAD_NOTIFY_THRESHOLD) {
    return null;
  }
  const { position, reading } = lead;
  const kind = known(InsurancePositionKind, position.kind);
  return {
    kind:
      kind === InsurancePositionKind.OOP_FAMILY ||
      kind === InsurancePositionKind.OOP_INDIVIDUAL
        ? "outOfPocket"
        : "deductible",
    family:
      kind === InsurancePositionKind.OOP_FAMILY ||
      kind === InsurancePositionKind.DEDUCTIBLE_FAMILY,
    outOfNetwork:
      known(InsurancePositionScope, position.scope) ===
      InsurancePositionScope.OUT_OF_NETWORK,
    delta: Math.round(lead.reading.delta),
    at: reading.observedAt,
  };
};

/** Whole dollars. What's left, when it's the limit less what's met, comes off the rounded two so they still add up. */
const figureOf = ({
  metAmount,
  limitAmount,
  remainingAmount,
}: Position): Figure => {
  const met = Math.round(metAmount);
  const limit = limitAmount == null ? null : Math.round(limitAmount);
  return {
    met,
    limit,
    remaining:
      remainingAmount == null
        ? null
        : limit !== null &&
            limitAmount != null &&
            Math.abs(limitAmount - metAmount - remainingAmount) < 0.01
          ? limit - met
          : Math.round(remainingAmount),
  };
};

const headlineOf = (
  own: Position[],
  kinds: typeof DEDUCTIBLES,
  kind: HeadlineFigure["kind"],
) => {
  const position = own.find(
    p => p.headline && (p.kind === kinds.individual || p.kind === kinds.family),
  );
  return position
    ? {
        position,
        figure: {
          ...figureOf(position),
          kind,
          tier:
            position.kind === kinds.family
              ? ("family" as const)
              : ("individual" as const),
        },
      }
    : undefined;
};

const figuresOf = (
  own: Position[],
  scope: InsurancePositionScope,
): NetworkFigures => {
  const pair = (kinds: typeof DEDUCTIBLES): FigurePair => {
    const find = (kind: InsurancePositionKind) => {
      const p = own.find(
        p =>
          p.kind === kind && known(InsurancePositionScope, p.scope) === scope,
      );
      return p ? figureOf(p) : null;
    };
    return { individual: find(kinds.individual), family: find(kinds.family) };
  };
  return { deductible: pair(DEDUCTIBLES), outOfPocket: pair(OUT_OF_POCKET) };
};

const networkHistories = (own: Position[], scope: InsurancePositionScope) => {
  const of = (kind: InsurancePositionKind) => {
    const p = own.find(
      p => p.kind === kind && known(InsurancePositionScope, p.scope) === scope,
    );
    return p ? historyOf(p) : [];
  };
  const tiers = (kinds: typeof DEDUCTIBLES): Record<Tier, ChartPoint[]> => ({
    individual: of(kinds.individual),
    family: of(kinds.family),
  });
  return { deductible: tiers(DEDUCTIBLES), outOfPocket: tiers(OUT_OF_POCKET) };
};

const historyOf = (position: Position): ChartPoint[] =>
  position.history
    // An ENDED reading carries no amount.
    .flatMap(r =>
      r.metAmount === null || r.metAmount === undefined
        ? []
        : [{ at: r.observedAt, met: Math.round(r.metAmount) }],
    )
    .reverse();

const ruleOf = ({ deductibleApplies }: Amount): DeductibleRule =>
  deductibleApplies === null || deductibleApplies === undefined
    ? "unknown"
    : deductibleApplies
      ? "applies"
      : "doesNotApply";

/**
 * The payer's default amount. With none named, nothing says which one a visit
 * gets: the one amount they all agree on, or the range.
 */
const shown = (amounts: Amount[]): PriceAmount | null => {
  const picked = amounts.find(a => a.default);
  if (picked) {
    return { kind: "one", amount: picked.amount, deductible: ruleOf(picked) };
  }
  const [rule, ...rules] = amounts.map(ruleOf);
  if (rule === undefined) {
    return null;
  }
  const deductible = rules.every(r => r === rule) ? rule : "unknown";
  const values = amounts.map(a => a.amount);
  const min = Math.min(...values);
  const max = Math.max(...values);
  return min === max
    ? { kind: "one", amount: min, deductible }
    : { kind: "range", min, max, deductible };
};

const SOURCES = [
  InsuranceCostShareSource.PAYER,
  InsuranceCostShareSource.PLAN_DOCUMENT,
  InsuranceCostShareSource.CARD,
  InsuranceCostShareSource.UNRECOGNIZED,
];

const inNetworkOf = (costShares: CostShare[]): CostShare[] =>
  SOURCES.flatMap(source =>
    [InsuranceNetwork.IN_NETWORK, InsuranceNetwork.UNKNOWN].flatMap(network =>
      costShares.filter(
        s =>
          known(InsuranceCostShareSource, s.source) === source &&
          known(InsuranceNetwork, s.network) === network,
      ),
    ),
  );

/** In-network prices: core services in order, then any other priced service. */
/**
 * Past the deductible a visit costs the share after it, whatever the rule.
 * With the family rule unknown, one person's met deductible beside a family
 * one doesn't settle it: as What you pay reads it.
 */
const deductibleMet = ({ individual, family }: FigurePair) => {
  const figure = family ?? individual;
  return (
    figure?.limit != null &&
    (figure.remaining ?? figure.limit - figure.met) <= 0
  );
};

/**
 * Past the deductible (or a $0 one), or with the max met, the out-of-pocket
 * max is the figure left to climb.
 */
export const maxLeads = ({ deductible, outOfPocket }: NetworkFigures) =>
  deductibleMet(deductible) || outOfPocketMet(outOfPocket);

/** Each person's count sits inside the family's, so either one met is met. */
const outOfPocketMet = ({ individual, family }: FigurePair) =>
  [individual, family].some(
    f =>
      f !== null &&
      (f.remaining !== null || f.limit !== null) &&
      (f.remaining ?? (f.limit ?? 0) - f.met) <= 0,
  );

const pricesOf = (inNetwork: CostShare[]): Price[] => {
  const order = [
    ...CORE_SERVICES,
    ...Object.values(InsuranceCareCategory).filter(
      c =>
        !CORE_SERVICES.includes(c) && c !== InsuranceCareCategory.UNRECOGNIZED,
    ),
  ];
  return order.flatMap(category => {
    const share = inNetwork.find(
      s =>
        s.category === category && (s.copays.length || s.coinsurances.length),
    );
    if (!share) {
      return [];
    }
    return [
      {
        category,
        copay: shown(share.copays),
        coinsurance: shown(share.coinsurances),
      },
    ];
  });
};

const CONTACTS: [
  PlanContact["key"],
  string,
  keyof NonNullable<Membership["contacts"]>,
][] = [
  ["memberServices", "Member services", "memberServicesPhone"],
  ["nurseLine", "Nurse line", "nurseLinePhone"],
  ["mentalHealth", "Mental health", "mentalHealthPhone"],
  ["pharmacyHelp", "Pharmacy help", "pharmacyHelpPhone"],
];

const contactsOf = (contacts: Membership["contacts"]): PlanContact[] =>
  CONTACTS.flatMap(([key, label, field]) => {
    const phone = contacts?.[field];
    return typeof phone === "string" && phone !== ""
      ? [{ key, label, phone }]
      : [];
  });
